/* mo-lineup-casper.js: the Casper look of sections/mo-brand-lineup.liquid (2026-10-05).
 * Loaded only when the section's Look is "Casper". Registers:
 *   MOLineupHeroes.zones   the hero showpiece: a sleeper on a Casper mattress drawn as a cutaway.
 *                          The foam carries the sleeper with spring physics: pick the support
 *                          (continuous, Zoned Support, Zoned Support Max) and watch where the
 *                          shoulder sinks in and the hips are held. A spirit level reads the spine.
 *                          Drag the sleeper up and drop them; roll them onto their back.
 *   MOLineupDraw.casper    the row drawings: a side view with the cover cut back, in Casper's
 *                          materials (open-cell Breathe foams, zoned and channel-cut Align foams,
 *                          phase change material, graphite HeatDelete bands, Core+ pillars, coils).
 * No libraries. Reduced motion: the settled scene, drawn once; nothing moves.
 * The foam is a spring-mass bed (after "Kinetic Matrix" by daiwiikharihar, 21st.dev id 24572): one
 * spring column per inch, stiffer or softer by zone, that densifies as it compresses. The sleeper
 * is a beam of weighted points on it, with the body's own weight by region, its breadth and its
 * bending stiffness. Picking up and dropping follows "Lanyard Badge" (kedhareswer, id 31036). */
(function () {
  'use strict';
  if (window.MOLineupCasper) return;
  window.MOLineupCasper = true;

  var still = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var heroes = window.MOLineupHeroes = window.MOLineupHeroes || {};
  var draws = window.MOLineupDraw = window.MOLineupDraw || {};

  function clamp(v, a, b) { return v < a ? a : v > b ? b : v; }
  function mix(a, b, t) { return a + (b - a) * t; }
  function smooth(e0, e1, x) { var t = clamp((x - e0) / (e1 - e0), 0, 1); return t * t * (3 - 2 * t); }
  function ease(t) { return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2; }
  function n1(v) { return Math.round(v * 10) / 10; }

  /* colors: parsed by a detached 2D canvas, so any CSS color works and nothing touches the page */
  var probe = null;
  function rgb(value, fallback) {
    value = (value || '').trim();
    if (!value) return fallback;
    if (!probe) probe = document.createElement('canvas').getContext('2d');
    if (!probe) return fallback;
    probe.fillStyle = '#010203'; probe.fillStyle = value;
    var s = probe.fillStyle, m;
    if (s === '#010203' && value.toLowerCase() !== '#010203') return fallback;
    if (s.charAt(0) === '#') return [parseInt(s.substr(1, 2), 16), parseInt(s.substr(3, 2), 16), parseInt(s.substr(5, 2), 16)];
    if ((m = /rgba?\(([^)]+)\)/.exec(s))) { var p = m[1].split(',').map(parseFloat); return [p[0], p[1], p[2]]; }
    return fallback;
  }
  function css(c, a) { return 'rgba(' + c[0] + ',' + c[1] + ',' + c[2] + ',' + (a == null ? 1 : a) + ')'; }
  function shade(c, f) { return [Math.round(clamp(c[0] * f, 0, 255)), Math.round(clamp(c[1] * f, 0, 255)), Math.round(clamp(c[2] * f, 0, 255))]; }
  function blend(a, b, t) { return [Math.round(mix(a[0], b[0], t)), Math.round(mix(a[1], b[1], t)), Math.round(mix(a[2], b[2], t))]; }

  /* ==================================================================================
   * The scene, in inches. The bed's long side faces the viewer; the head end is at x = 0.
   * A Queen is 80 in. long. The mattress sits on a low foundation.
   * ================================================================================== */
  var BED_L = 80, PLINTH = 1.6;
  var PILLOW = { x0: 1, x1: 20, h: 4.6 };
  var G = 260;                       /* in/s^2: a little under real gravity, so a drop reads */
  var BODY_L = 67, N = 68;           /* body nodes, one per inch from the top of the head */

  /* The three support systems, from Casper's own descriptions. Zone factors multiply the foam's
     stiffness under that part of the bed (above 1 is firmer). Layer lists are top to bottom; the
     depths are illustrative (Casper does not publish them), the total height is the model's. */
  var SUPPORT = {
    continuous: {
      height: 11, base: 1.1,
      zones: [[0, 80, 1]],
      layers: [['cover', 0.55], ['breathe', 2.4], ['align', 2.6], ['core', 5.45]]
    },
    zoned: {
      height: 12, base: 1.0,
      /* softer around the shoulders, firmer under the hips, waist and lower back */
      zones: [[0, 26, 0.45], [26, 50, 1.6], [50, 80, 1.0]],
      layers: [['cover', 0.55], ['breathe', 2.0], ['align-zoned', 2.45], ['coils', 6.0], ['base', 1.0]]
    },
    max: {
      height: 14, base: 0.95,
      /* two zoned layers, seven zones of precisely cut channels */
      zones: [[0, 10, 0.9], [10, 25, 0.44], [25, 30, 0.95], [30, 38, 1.75], [38, 48, 1.55], [48, 64, 1.0], [64, 80, 0.85]],
      layers: [['cover', 0.6], ['breathe', 2.2], ['breathe-zoned', 1.9], ['align-channels', 2.3], ['coils', 6.0], ['base', 1.0]]
    }
  };
  /* how much of a dent each layer takes, per inch of its depth */
  var GIVE = { cover: 0.6, breathe: 1.25, 'breathe-zoned': 1.15, align: 1.0, 'align-zoned': 1.0, 'align-channels': 1.0, coils: 0.42, core: 0.34, base: 0.03 };

  function zoneAt(sup, x) {
    /* zone factor with soft 2 in. blends between zones */
    var z = sup.zones, f = z[0][2], i;
    for (i = 0; i < z.length; i++) if (x >= z[i][0]) f = z[i][2];
    for (i = 1; i < z.length; i++) {
      var b = z[i][0], d = x - b;
      if (Math.abs(d) < 2) return mix(z[i - 1][2], z[i][2], smooth(-2, 2, d));
    }
    return f;
  }

  /* ---------- the sleeper ----------
     Profiles along the body, s from the top of the head (0) to the soles (1), in inches:
     w = centre line to the surface that meets the bed, u = centre line to the far surface,
     c = contact breadth across the bed, m = weight share, e = bending stiffness.
     Side: an average adult seen from the front, lying on one side, legs stacked.
     Back: the same person seen from the side, lying on their back, toes up. */
  var SIDE = [
    /* s,     w,   u,   c,   m,   e */
    [0.000, 0.6, 0.6, 2.0, 0.6, 30],
    [0.030, 2.4, 2.4, 3.0, 1.5, 30],
    [0.070, 3.3, 3.3, 4.0, 1.9, 30],
    [0.115, 2.9, 2.9, 3.6, 1.4, 22],
    [0.150, 2.2, 2.3, 2.6, 0.5, 4],
    [0.175, 4.8, 5.0, 3.6, 1.8, 30],
    [0.205, 8.4, 8.6, 5.0, 3.9, 60],
    [0.250, 7.6, 8.0, 5.0, 3.7, 60],
    [0.310, 6.3, 6.9, 4.6, 3.4, 52],
    [0.380, 5.0, 5.9, 3.4, 3.1, 46],
    [0.430, 6.4, 6.9, 5.4, 3.5, 40],
    [0.475, 7.3, 7.6, 6.6, 4.2, 70],
    [0.540, 6.6, 6.8, 5.6, 3.6, 50],
    [0.640, 5.2, 5.2, 4.6, 2.6, 40],
    [0.720, 4.0, 4.0, 3.0, 1.6, 10],
    [0.820, 4.1, 4.1, 3.6, 1.6, 30],
    [0.930, 2.6, 2.6, 2.4, 0.9, 20],
    [0.975, 2.4, 2.7, 2.2, 0.6, 10],
    [1.000, 1.0, 1.4, 1.0, 0.2, 10]
  ];
  var BACK = [
    [0.000, 0.8, 0.8, 2.0, 0.6, 30],
    [0.030, 2.8, 2.6, 3.6, 1.5, 30],
    [0.070, 3.6, 3.9, 4.6, 1.9, 30],
    [0.100, 3.2, 4.3, 4.0, 1.4, 22],
    [0.130, 2.4, 3.2, 3.0, 0.9, 22],
    [0.160, 1.7, 2.2, 1.0, 0.5, 4],
    [0.200, 3.6, 3.6, 10.0, 3.0, 60],
    [0.260, 4.2, 4.8, 12.0, 3.8, 60],
    [0.330, 3.8, 4.6, 10.0, 3.6, 56],
    [0.390, 2.8, 4.4, 6.0, 3.3, 46],
    [0.450, 4.3, 3.9, 11.0, 3.9, 40],
    [0.500, 4.6, 3.3, 12.0, 4.0, 70],
    [0.560, 3.3, 3.2, 10.0, 3.6, 50],
    [0.680, 2.8, 2.8, 9.0, 2.6, 40],
    [0.740, 2.0, 2.8, 4.0, 1.6, 10],
    [0.840, 2.4, 2.0, 7.0, 1.6, 30],
    [0.940, 1.6, 1.6, 4.0, 0.9, 20],
    [0.965, 1.4, 4.2, 3.0, 0.6, 10],
    [1.000, 0.8, 7.2, 1.2, 0.2, 10]
  ];
  function profile(table, s, col) {
    for (var i = 1; i < table.length; i++) {
      if (s <= table[i][0]) {
        var a = table[i - 1], b = table[i], t = (s - a[0]) / (b[0] - a[0]);
        t = t * t * (3 - 2 * t);
        return mix(a[col], b[col], t);
      }
    }
    return table[table.length - 1][col];
  }

  function Sleeper() {
    var y = new Float32Array(N), v = new Float32Array(N), w = new Float32Array(N), u = new Float32Array(N);
    var c = new Float32Array(N), m = new Float32Array(N), e = new Float32Array(N), s = new Float32Array(N);
    var WEIGHT = 170;  /* lb */
    function setPose(t) {
      /* t: 0 on the side, 1 on the back */
      var sum = 0, i;
      for (i = 0; i < N; i++) {
        s[i] = i / (N - 1);
        w[i] = mix(profile(SIDE, s[i], 1), profile(BACK, s[i], 1), t);
        u[i] = mix(profile(SIDE, s[i], 2), profile(BACK, s[i], 2), t);
        c[i] = mix(profile(SIDE, s[i], 3), profile(BACK, s[i], 3), t);
        m[i] = mix(profile(SIDE, s[i], 4), profile(BACK, s[i], 4), t);
        e[i] = mix(profile(SIDE, s[i], 5), profile(BACK, s[i], 5), t);
        sum += m[i];
      }
      for (i = 0; i < N; i++) m[i] = m[i] / sum * WEIGHT;
    }
    setPose(0);
    return { y: y, v: v, w: w, u: u, c: c, m: m, e: e, s: s, setPose: setPose, x0: 7.4, dx: BODY_L / (N - 1) };
  }

  /* ---------- the bed: rest surface, stiffness ---------- */
  function Bed() {
    var cols = 161;                                  /* half-inch columns */
    var dent = new Float32Array(cols);               /* how far the top surface is pressed down */
    var pdent = new Float32Array(cols);              /* the pillow's own dent */
    return { cols: cols, dx: BED_L / (cols - 1), dent: dent, pdent: pdent };
  }

  /* ==================================================================================
   * The hero
   * ================================================================================== */
  heroes.zones = function (root) {
    var hero = root.querySelector('[data-mo-lineup-hero]');
    var stage = hero && hero.querySelector('.mo-lineup__stage');
    var canvas = stage && stage.querySelector('canvas');
    if (!canvas || !canvas.getContext) return function () {};
    var ctx = canvas.getContext('2d');
    if (!ctx) return function () {};

    var body = Sleeper(), bed = Bed();
    var mode = stage.getAttribute('data-support') || 'zoned';
    var supFrom = SUPPORT[mode], supTo = SUPPORT[mode], supT = 1;      /* support blend while switching */
    var pose = 0, poseTo = 0;                                           /* 0 side, 1 back */
    var lift = 0, held = null, liftV = 0, shiftX = 0;
    var levelEl = stage.querySelector('[data-level]'), bubbleEl = levelEl && levelEl.querySelector('[data-bubble]');
    var levelText = stage.querySelector('[data-level-text]'), statusEl = stage.querySelector('[data-zones-status]');
    var labelsOn = 0;

    /* ---------- the support in effect at x, blended while switching ---------- */
    function height() { return mix(supFrom.height, supTo.height, ease(supT)); }
    function top() { return PLINTH + height(); }
    function stiff(x) {
      var a = supFrom.base * zoneAt(supFrom, x), b = supTo.base * zoneAt(supTo, x);
      return mix(a, b, ease(supT));
    }
    function pillowTop(x) {
      /* a soft rounded pillow: full height in the middle, rounded ends */
      if (x <= PILLOW.x0 || x >= PILLOW.x1) return 0;
      var t = (x - PILLOW.x0) / (PILLOW.x1 - PILLOW.x0);
      return PILLOW.h * Math.pow(Math.sin(Math.PI * t), 0.42);
    }

    /* ---------- physics ----------
       The foam pushes on each body point in proportion to how far the body's lower surface sits
       below the rest surface (bed top, or pillow top), times the foam's stiffness there and the
       body's breadth across the bed. Foam firms up as it is pressed (densification). Neighbouring
       points resist bending (the body's stiffness). */
    var TUNE = window.__moCzTune || {};
    if (TUNE.zoned) SUPPORT.zoned.zones = TUNE.zoned;
    if (TUNE.max) SUPPORT.max.zones = TUNE.max;
    if (TUNE.base) { SUPPORT.continuous.base = TUNE.base[0]; SUPPORT.zoned.base = TUNE.base[1]; SUPPORT.max.base = TUNE.base[2]; }
    var K = TUNE.K || 0.18;     /* foam stiffness, lb per cubic inch, before zoning */
    var KP = TUNE.KP || 0.05;    /* pillow */
    var DENS = TUNE.DENS || 4; /* depth, in., where the foam starts to firm up fast */
    var prev = new Float32Array(N);
    function surfaceAt(x) { return top() + pillowTop(x); }
    /* bending stiffness between neighbours, 0 to 1 per pass (position-based) */
    function bendStiff(i) { return clamp(body.e[i] / 70, 0.004, 1); }
    function step(h) {
      var y = body.y, w = body.w, c = body.c, m = body.m, i, x, f, p, a, T = top();
      /* Verlet: the move since the last step is the velocity */
      var damp = Math.exp(-h * (TUNE.damp || 5.5));
      for (i = 0; i < N; i++) {
        x = body.x0 + shiftX + i * body.dx;
        var acc = -G, b = y[i] - w[i], ph = pillowTop(x);
        if (ph > 0) {
          p = T + ph - b;
          if (p > 0) {
            var pp = Math.min(p, ph * 0.9);
            f = KP * c[i] * pp * (1 + Math.pow(pp / (ph * 0.6), 4) * 3);
            acc += G * f / m[i];
          }
        }
        p = T - b;
        if (p > 0) {
          a = stiff(x);
          f = K * a * c[i] * p * (1 + Math.pow(p / DENS, 4) * 1.8);
          acc += G * f / m[i];
        }
        var vel = (y[i] - prev[i]) * damp;
        prev[i] = y[i];
        y[i] += vel + acc * h * h;
      }
      /* the body's own stiffness: keep each three neighbours in line, weighted by mass, so the
         torso and pelvis move as near-rigid pieces and the waist, neck and knees can bend */
      for (var it = 0; it < 6; it++) {
        for (i = 1; i < N - 1; i++) {
          var k = bendStiff(i);
          var C2 = y[i - 1] - 2 * y[i] + y[i + 1];
          var wa = 1 / m[i - 1], wb = 1 / m[i], wc = 1 / m[i + 1];
          var lam = -C2 / (wa + 4 * wb + wc) * k;
          y[i - 1] += lam * wa; y[i] -= 2 * lam * wb; y[i + 1] += lam * wc;
        }
      }
      /* someone holding the sleeper: every point follows the hand */
      if (held) {
        for (i = 0; i < N; i++) {
          var tgt = held.base[i] + held.lift;
          y[i] += (tgt - y[i]) * 0.3;
        }
      }
    }
    function velocity(i, h) { return (body.y[i] - prev[i]) / h; }
    function kick(v, h) { for (var i = 0; i < N; i++) prev[i] = body.y[i] - v * h; }
    function settle(n) { for (var k = 0; k < n; k++) step(1 / 240); }
    function placeAbove(dy) {
      var T = top();
      for (var i = 0; i < N; i++) {
        var x = body.x0 + shiftX + i * body.dx;
        body.y[i] = T + pillowTop(x) * 0.6 + body.w[i] + dy;
        prev[i] = body.y[i];
      }
    }
    /* the bed's dent under the sleeper, for drawing: the body's lower surface, interpolated
       between body points, pressed into the pillow first and the mattress under it; then spread
       a little by the cover (a soft shoulder to each dent) */
    var rawB = null, rawP = null, tmpB = null;
    function dents() {
      var T = top(), j, x, i;
      if (!rawB) { rawB = new Float32Array(bed.cols); rawP = new Float32Array(bed.cols); tmpB = new Float32Array(bed.cols); }
      for (j = 0; j < bed.cols; j++) {
        x = j * bed.dx;
        var fi = (x - body.x0 - shiftX) / body.dx, dB = 0, dP = 0;
        if (fi >= 0 && fi <= N - 1) {
          var i0 = Math.min(N - 2, Math.floor(fi)), t = fi - i0;
          var b = mix(body.y[i0] - body.w[i0], body.y[i0 + 1] - body.w[i0 + 1], t), ph = pillowTop(x);
          var pin = Math.max(0, T + ph - b);
          if (ph > 0) { dP = Math.min(pin, ph * 0.9); dB = Math.max(0, pin - dP) ; }
          else dB = pin;
        }
        rawB[j] = dB; rawP[j] = dP;
      }
      /* the pillow also presses the mattress under it a little */
      for (j = 0; j < bed.cols; j++) { x = j * bed.dx; var ph2 = pillowTop(x); if (ph2 > 0) rawB[j] = Math.max(rawB[j], rawP[j] * 0.18); }
      blur(rawB, tmpB, 5); blur(tmpB, bed.dent, 5);
      for (j = 0; j < bed.cols; j++) bed.dent[j] = Math.max(rawB[j] * 0.92, bed.dent[j] * 1.05);
      blur(bed.dent, tmpB, 1); bed.dent.set(tmpB);
      blur(rawP, tmpB, 2); blur(tmpB, bed.pdent, 2);
      for (j = 0; j < bed.cols; j++) bed.pdent[j] = Math.max(bed.pdent[j], rawP[j] * 0.9);
    }
    function blur(src, dst, r) {
      for (var j = 0; j < src.length; j++) {
        var s2 = 0, wsum = 0;
        for (var k = -r; k <= r; k++) {
          var jj = j + k; if (jj < 0 || jj >= src.length) continue;
          var wgt = r + 1 - Math.abs(k); s2 += src[jj] * wgt; wsum += wgt;
        }
        dst[j] = s2 / wsum;
      }
    }
    /* the spine: neck (C7) to sacrum, and a reading of how level it is */
    function spine() {
      var a = Math.round(0.2 * (N - 1)), b = Math.round(0.475 * (N - 1));
      var ya = body.y[a], yb = body.y[b], xa = a * body.dx, xb = b * body.dx;
      var tilt = Math.atan2(ya - yb, xb - xa) * 180 / Math.PI;   /* positive: shoulders higher */
      var sag = 0;
      for (var i = a; i <= b; i++) {
        var t = (i - a) / (b - a), line = mix(ya, yb, t);
        if (line - body.y[i] > sag) sag = line - body.y[i];
      }
      return { a: a, b: b, tilt: tilt, sag: sag };
    }

    /* ---------- drawing ---------- */
    var W = 0, H = 0, dpr = 1, S = 1, OX = 0, OY = 0, VIEW = null;
    var C = {};
    function readColors() {
      var st = getComputedStyle(stage);
      function c(name, fb) { return rgb(st.getPropertyValue(name), fb); }
      C.ink = c('--cz-ink', [27, 28, 29]); C.navy = c('--cz-navy', [30, 48, 110]); C.sleeper = c('--cz-sleeper', [30, 48, 110]);
      C.sleeperHi = c('--cz-sleeper-hi', [82, 102, 153]); C.spine = c('--cz-spine', [255, 255, 255]);
      C.cover = c('--cz-cover', [244, 244, 240]); C.knit = c('--cz-knit', [200, 205, 214]);
      C.breathe = c('--cz-breathe', [243, 238, 228]); C.hole = c('--cz-hole', [214, 203, 182]);
      C.align = c('--cz-align', [219, 226, 240]); C.soft = c('--cz-soft', [232, 237, 248]); C.firm = c('--cz-firm', [170, 186, 222]);
      C.core = c('--cz-core', [226, 222, 214]); C.pore = c('--cz-pore', [196, 189, 176]);
      C.coilBg = c('--cz-coil-bg', [246, 245, 242]); C.coil = c('--cz-coil', [96, 102, 112]); C.pocket = c('--cz-pocket', [205, 202, 196]);
      C.base = c('--cz-base', [64, 70, 82]); C.plinth = c('--cz-plinth', [52, 58, 70]);
      C.pillow = c('--cz-pillow', [250, 250, 248]); C.pillowLine = c('--cz-pillow-line', [210, 212, 218]);
      C.duvet = c('--cz-duvet', [252, 252, 250]); C.duvetLo = c('--cz-duvet-lo', [228, 232, 240]); C.duvetLine = c('--cz-duvet-line', [196, 202, 214]); C.hem = c('--cz-hem', [221, 228, 243]);
      C.shadow = c('--cz-shadow', [30, 26, 22]); C.label = c('--cz-label', [90, 86, 82]); C.accent = c('--cz-accent', [188, 0, 12]);
      C.dark = document.documentElement.getAttribute('data-mo-scheme') === 'dark';
    }
    function layout() {
      var box = canvas.getBoundingClientRect();
      W = box.width; H = box.height;
      if (!W || !H) { VIEW = null; return; }
      dpr = Math.min(2, Math.max(1, window.devicePixelRatio || 1));
      canvas.width = Math.round(W * dpr); canvas.height = Math.round(H * dpr);
      /* the view: the whole bed and the sleeper above it, fitted to the width */
      /* one fixed frame for every screen, centred like the still under the canvas
         (object-fit: contain), so the live drawing lands exactly on the still */
      VIEW = { x0: -3, x1: 83, y0: -3.2, y1: 31.5 };
      S = Math.min(W / (VIEW.x1 - VIEW.x0), H / (VIEW.y1 - VIEW.y0));
      OX = (W - (VIEW.x1 - VIEW.x0) * S) / 2 - VIEW.x0 * S;
      OY = H - (H - (VIEW.y1 - VIEW.y0) * S) * 0.5 + VIEW.y0 * S;
    }
    function X(x) { return OX + x * S; }
    function Y(y) { return OY - y * S; }

    /* how far a point at depth z (inches below the rest top) moves down, under a top dent d */
    var stack = null;
    function buildStack() {
      /* blend the two supports' layer lists: draw the target's stack, at the blended height */
      var sup = supT < 0.5 ? supFrom : supTo, hgt = height(), tot = 0, i;
      sup.layers.forEach(function (l) { tot += l[1]; });
      var f = hgt / tot, z = 0, give = 0, out = [];
      for (i = 0; i < sup.layers.length; i++) {
        var d = sup.layers[i][1] * f;
        out.push({ kind: sup.layers[i][0], z0: z, z1: z + d });
        give += (GIVE[sup.layers[i][0]] || 0.5) * d;
        z += d;
      }
      /* share of the dent still to come below each boundary */
      var acc = give;
      out.forEach(function (l) { l.g0 = acc / give; acc -= (GIVE[l.kind] || 0.5) * (l.z1 - l.z0); l.g1 = Math.max(0, acc / give); });
      stack = { layers: out, sup: sup };
    }
    function dentAt(x) {
      var j = x / bed.dx, j0 = Math.floor(j), t = j - j0;
      j0 = clamp(j0, 0, bed.cols - 2);
      return mix(bed.dent[j0], bed.dent[j0 + 1], t);
    }
    /* the height of a boundary at rest depth z, at x */
    function boundary(x, z, g) { return top() - z - dentAt(x) * g; }

    function pathAlong(yfn, x0, x1, step, move) {
      for (var x = x0; x <= x1 + 1e-6; x += step) {
        var px = X(Math.min(x, x1)), py = Y(yfn(Math.min(x, x1)));
        if (move && x === x0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
      }
    }

    function drawGlow() {
      var cxp = X(BED_L * 0.5), cyp = Y(top() + 6), rad = BED_L * 0.62 * S;
      var g = ctx.createRadialGradient(cxp, cyp, 0, cxp, cyp, rad);
      g.addColorStop(0, css(C.navy, C.dark ? 0.16 : 0.07)); g.addColorStop(1, css(C.navy, 0));
      ctx.save(); ctx.translate(cxp, cyp); ctx.scale(1, 0.42); ctx.translate(-cxp, -cyp);
      ctx.fillStyle = g; ctx.beginPath(); ctx.arc(cxp, cyp, rad, 0, Math.PI * 2); ctx.fill(); ctx.restore();
    }
    function drawBed() {
      var T = top(), L = stack.layers, i, x;
      var r = 1.2;
      /* the floor shadow and the foundation */
      var grd = ctx.createRadialGradient(X(BED_L / 2), Y(0), 0, X(BED_L / 2), Y(0), (BED_L / 2 + 6) * S);
      grd.addColorStop(0, css(C.shadow, C.dark ? 0.5 : 0.16)); grd.addColorStop(1, css(C.shadow, 0));
      ctx.save(); ctx.translate(0, Y(0)); ctx.scale(1, 0.09); ctx.translate(0, -Y(0));
      ctx.fillStyle = grd; ctx.beginPath(); ctx.arc(X(BED_L / 2), Y(0), (BED_L / 2 + 6) * S, 0, Math.PI * 2); ctx.fill(); ctx.restore();
      ctx.fillStyle = css(C.plinth);
      roundRect(X(1.5), Y(PLINTH), (BED_L - 3) * S, (PLINTH - 0.15) * S, 2 * S * 0.4); ctx.fill();
      [6, BED_L - 6].forEach(function (lx) { ctx.fillRect(X(lx) - 0.8 * S, Y(0.2), 1.6 * S, 0.2 * S); });

      /* clip to the mattress, its top following the dent */
      ctx.save();
      ctx.beginPath();
      ctx.moveTo(X(0), Y(PLINTH + r));
      ctx.lineTo(X(0), Y(T - r));
      ctx.quadraticCurveTo(X(0), Y(T), X(r), Y(T));
      pathAlong(function (xx) { return boundary(xx, 0, 1); }, r, BED_L - r, 0.5, false);
      ctx.quadraticCurveTo(X(BED_L), Y(T), X(BED_L), Y(T - r));
      ctx.lineTo(X(BED_L), Y(PLINTH + r));
      ctx.quadraticCurveTo(X(BED_L), Y(PLINTH), X(BED_L - r), Y(PLINTH));
      ctx.lineTo(X(r), Y(PLINTH));
      ctx.quadraticCurveTo(X(0), Y(PLINTH), X(0), Y(PLINTH + r));
      ctx.closePath();
      ctx.save();
      ctx.clip();
      for (i = 0; i < L.length; i++) drawLayer(L[i]);
      ctx.restore();
      /* outline */
      ctx.strokeStyle = css(C.ink, C.dark ? 0.55 : 0.32); ctx.lineWidth = Math.max(1, S * 0.18);
      ctx.stroke();
      ctx.restore();
    }
    function roundRect(x, y, w, h, r) {
      ctx.beginPath(); ctx.moveTo(x + r, y); ctx.lineTo(x + w - r, y); ctx.quadraticCurveTo(x + w, y, x + w, y + r);
      ctx.lineTo(x + w, y + h - r); ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h); ctx.lineTo(x + r, y + h);
      ctx.quadraticCurveTo(x, y + h, x, y + h - r); ctx.lineTo(x, y + r); ctx.quadraticCurveTo(x, y, x + r, y); ctx.closePath();
    }
    function band(l, fill) {
      ctx.beginPath();
      pathAlong(function (x) { return boundary(x, l.z0, l.g0); }, 0, BED_L, 0.5, true);
      for (var x = BED_L; x >= -1e-6; x -= 0.5) ctx.lineTo(X(Math.max(0, x)), Y(boundary(Math.max(0, x), l.z1, l.g1)));
      ctx.closePath();
      ctx.fillStyle = fill; ctx.fill();
    }
    function zoneColor(x) {
      var f = stiff(x) / (supT < 0.5 ? supFrom.base : supTo.base);
      var t = clamp((f - 0.55) / (1.55 - 0.55), 0, 1);
      return blend(C.soft, C.firm, t);
    }
    function drawLayer(l) {
      var k = l.kind, x, z, yy, i;
      if (k === 'align-zoned' || k === 'align-channels' || k === 'breathe-zoned') {
        /* zones: colour by local stiffness, one column at a time */
        for (x = 0; x < BED_L; x += 0.5) {
          ctx.beginPath();
          ctx.moveTo(X(x), Y(boundary(x, l.z0, l.g0)));
          ctx.lineTo(X(x + 0.55), Y(boundary(x + 0.5, l.z0, l.g0)));
          ctx.lineTo(X(x + 0.55), Y(boundary(x + 0.5, l.z1, l.g1)));
          ctx.lineTo(X(x), Y(boundary(x, l.z1, l.g1)));
          ctx.closePath();
          var zc = zoneColor(x + 0.25);
          if (k === 'breathe-zoned') zc = blend(C.breathe, zc, 0.45);
          ctx.fillStyle = css(zc); ctx.fill();
        }
        /* zone seams */
        var sup = supT < 0.5 ? supFrom : supTo;
        ctx.strokeStyle = css(C.ink, 0.18 * zonesVis()); ctx.lineWidth = 1;
        for (i = 1; i < sup.zones.length; i++) {
          x = sup.zones[i][0];
          ctx.beginPath(); ctx.moveTo(X(x), Y(boundary(x, l.z0, l.g0))); ctx.lineTo(X(x), Y(boundary(x, l.z1, l.g1))); ctx.stroke();
        }
        if (k === 'align-channels') {
          /* precisely cut channels, closer together where the foam is softer */
          ctx.strokeStyle = css(C.navy, 0.28 * zonesVis()); ctx.lineWidth = Math.max(1, S * 0.16);
          for (x = 0.8; x < BED_L - 0.5;) {
            var f = stiff(x) / sup.base, gap = 0.9 + Math.max(0, f - 0.5) * 2.4;
            if (f < 1.2) {
              ctx.beginPath(); ctx.moveTo(X(x), Y(boundary(x, l.z0, l.g0) - 0.35)); ctx.lineTo(X(x), Y(boundary(x, l.z1, l.g1) + 0.35)); ctx.stroke();
            }
            x += gap;
          }
        }
        if (k === 'breathe-zoned') holes(l, 1.6);
        return;
      }
      var fill = { cover: C.cover, breathe: C.breathe, align: C.align, core: C.core, coils: C.coilBg, base: C.base }[k] || C.core;
      band(l, css(fill));
      if (k === 'breathe') holes(l, 1.25);
      if (k === 'core') pores(l, 1.7, 0.16);
      if (k === 'align') pores(l, 1.4, 0.12);
      if (k === 'cover') {
        ctx.strokeStyle = css(C.knit); ctx.lineWidth = Math.max(0.8, S * 0.1);
        ctx.beginPath();
        var mid = (l.z0 + l.z1) / 2, up = 1;
        for (x = 0; x <= BED_L; x += 0.6, up = -up) {
          yy = boundary(x, mid, mix(l.g0, l.g1, 0.5)) + up * 0.12;
          if (x === 0) ctx.moveTo(X(x), Y(yy)); else ctx.lineTo(X(x), Y(yy));
        }
        ctx.stroke();
      }
      if (k === 'coils') coils(l);
      /* a soft light on the top of each layer, a little shade at its base */
      var g = ctx.createLinearGradient(0, Y(top() - l.z0), 0, Y(top() - l.z1));
      g.addColorStop(0, 'rgba(255,255,255,' + (C.dark ? 0.06 : 0.22) + ')'); g.addColorStop(0.5, 'rgba(255,255,255,0)'); g.addColorStop(1, 'rgba(0,0,0,' + (C.dark ? 0.16 : 0.06) + ')');
      band(l, g);
    }
    function holes(l, pitch) {
      /* open-cell foam: rows of small round cells that squash with the foam */
      ctx.fillStyle = css(C.hole, 0.9);
      var rows = Math.max(1, Math.floor((l.z1 - l.z0) / 0.75));
      for (var r = 0; r < rows; r++) {
        var z = l.z0 + (r + 0.5) * (l.z1 - l.z0) / rows, g = mix(l.g0, l.g1, (r + 0.5) / rows);
        for (var x = 0.8 + (r % 2) * pitch / 2; x < BED_L - 0.4; x += pitch) {
          var y0 = boundary(x, l.z0, l.g0), y1 = boundary(x, l.z1, l.g1), squash = (y0 - y1) / (l.z1 - l.z0);
          var yy = mix(y0, y1, (r + 0.5) / rows);
          ctx.beginPath(); ctx.ellipse(X(x), Y(yy), 0.26 * S, Math.max(0.3, 0.2 * S * squash), 0, 0, Math.PI * 2); ctx.fill();
        }
      }
    }
    function pores(l, pitch, rad) {
      ctx.fillStyle = css(C.pore, 0.7);
      var rows = Math.max(1, Math.floor((l.z1 - l.z0) / 0.9));
      for (var r = 0; r < rows; r++) {
        for (var x = 0.6 + (r % 2) * pitch / 2; x < BED_L - 0.3; x += pitch) {
          var y0 = boundary(x, l.z0, l.g0), y1 = boundary(x, l.z1, l.g1), yy = mix(y0, y1, (r + 0.5) / rows);
          ctx.beginPath(); ctx.arc(X(x + ((r * 7 + x * 3) % 0.5)), Y(yy), Math.max(0.5, rad * S), 0, Math.PI * 2); ctx.fill();
        }
      }
    }
    function coils(l) {
      var pitch = 2.6, x, k;
      for (x = 1.6; x < BED_L - 1; x += pitch) {
        var y0 = boundary(x, l.z0, l.g0), y1 = boundary(x, l.z1, l.g1), hgt = y0 - y1;
        /* the pocket */
        ctx.strokeStyle = css(C.pocket); ctx.lineWidth = Math.max(0.8, S * 0.08);
        roundRect(X(x - pitch * 0.46), Y(y0 - 0.15), pitch * 0.92 * S, (hgt - 0.3) * S, 0.8 * S); ctx.stroke();
        /* the coil, seen from the side: a helix, the far half fainter */
        var turns = 5, rx = pitch * 0.3, ry = Math.min(0.45, hgt / turns * 0.32), steps = turns * 14;
        var front = new Path2D(), back = new Path2D(), on = -1;
        for (k = 0; k <= steps; k++) {
          var a = k / steps * turns * Math.PI * 2, px = X(x + Math.cos(a) * rx), py = Y(y0 - 0.45 - (hgt - 0.9) * k / steps + Math.sin(a) * ry);
          var near = Math.sin(a) >= 0 ? 1 : 0;
          if (near !== on) { (near ? front : back).moveTo(px, py); on = near; } else (near ? front : back).lineTo(px, py);
        }
        ctx.lineCap = 'round';
        ctx.strokeStyle = css(C.coil, 0.32); ctx.lineWidth = Math.max(0.8, S * 0.12); ctx.stroke(back);
        ctx.strokeStyle = css(C.coil, 0.95); ctx.lineWidth = Math.max(1, S * 0.16); ctx.stroke(front);
      }
    }

    function pdentAt(x) {
      var j = x / bed.dx, j0 = clamp(Math.floor(j), 0, bed.cols - 2), t = j - j0;
      return mix(bed.pdent[j0], bed.pdent[j0 + 1], t);
    }
    function drawPillow() {
      var T = top(), x, pts = [];
      for (x = PILLOW.x0; x <= PILLOW.x1 + 1e-6; x += 0.25) pts.push([X(x), Y(T - dentAt(x) + Math.max(0.25, pillowTop(x) - pdentAt(x)))]);
      ctx.beginPath();
      ctx.moveTo(pts[0][0], pts[0][1]);
      for (var i = 1; i < pts.length; i++) ctx.lineTo(pts[i][0], pts[i][1]);
      for (x = PILLOW.x1; x >= PILLOW.x0 - 1e-6; x -= 0.5) ctx.lineTo(X(x), Y(T - dentAt(x) + 0.05));
      ctx.closePath();
      var g = ctx.createLinearGradient(0, Y(T + PILLOW.h), 0, Y(T));
      g.addColorStop(0, css(C.pillow)); g.addColorStop(1, css(shade(C.pillow, C.dark ? 0.78 : 0.9)));
      ctx.fillStyle = g; ctx.fill();
      ctx.strokeStyle = css(C.pillowLine); ctx.lineWidth = Math.max(1, S * 0.13); ctx.stroke();
      /* the seam round the middle of the pillow */
      ctx.strokeStyle = css(C.pillowLine, 0.8); ctx.lineWidth = Math.max(0.8, S * 0.08);
      ctx.beginPath();
      for (x = PILLOW.x0 + 0.6; x <= PILLOW.x1 - 0.6; x += 0.5) {
        var yy = T - dentAt(x) + Math.max(0.2, pillowTop(x) - pdentAt(x)) * 0.5;
        if (x === PILLOW.x0 + 0.6) ctx.moveTo(X(x), Y(yy)); else ctx.lineTo(X(x), Y(yy));
      }
      ctx.stroke();
    }

    /* the sleeper: the head on the pillow, the body under a duvet. The body is drawn first, very
       faintly, so the shoulder and hip show through the duvet like a shadow. */
    function catmull(pts, close) {
      var n = pts.length, i;
      ctx.moveTo(pts[0][0], pts[0][1]);
      var last = close ? n : n - 1;
      for (i = 0; i < last; i++) {
        var p0 = pts[close ? (i - 1 + n) % n : Math.max(0, i - 1)], p1 = pts[i], p2 = pts[(i + 1) % n], p3 = pts[close ? (i + 2) % n : Math.min(n - 1, i + 2)];
        ctx.bezierCurveTo(p1[0] + (p2[0] - p0[0]) / 6, p1[1] + (p2[1] - p0[1]) / 6, p2[0] - (p3[0] - p1[0]) / 6, p2[1] - (p3[1] - p1[1]) / 6, p2[0], p2[1]);
      }
    }
    function bx(i) { return body.x0 + shiftX + i * body.dx; }
    function drawSleeper() {
      var y = body.y, i, pts = [], k, x;
      var NECK = Math.round(0.155 * (N - 1));
      var back = pose > 0.5;
      /* the head: an oval resting on the pillow, tipped with the neck; a short neck behind the hem */
      var hc = Math.round(0.07 * (N - 1)), hy = y[hc], tilt = Math.atan2((y[hc + 3] - y[hc - 3]), 6 * body.dx);
      var hrx = 4.7, hry = mix(3.35, 3.9, pose);
      ctx.save();
      ctx.translate(X(bx(hc)), Y(hy));
      ctx.rotate(-tilt);
      var gh = ctx.createLinearGradient(0, -hry * S, 0, hry * S);
      gh.addColorStop(0, css(C.sleeperHi)); gh.addColorStop(1, css(C.sleeper));
      ctx.fillStyle = gh;
      ctx.beginPath(); ctx.ellipse(0, 0, hrx * S, hry * S, 0, 0, Math.PI * 2); ctx.fill();
      /* seen from behind: the line of the hair, and an ear on the side */
      ctx.globalAlpha = 1 - pose;
      ctx.strokeStyle = css(C.sleeperHi, 0.9); ctx.lineWidth = Math.max(1, S * 0.16); ctx.lineCap = 'round';
      ctx.beginPath(); ctx.ellipse(-0.6 * S, 0.1 * S, hrx * 0.7 * S, hry * 0.62 * S, 0, Math.PI * 1.05, Math.PI * 1.75); ctx.stroke();
      ctx.fillStyle = css(shade(C.sleeperHi, 1.08));
      ctx.beginPath(); ctx.ellipse(1.5 * S, 0.6 * S, 0.95 * S, 1.3 * S, 0.2, 0, Math.PI * 2); ctx.fill();
      ctx.globalAlpha = 1;
      ctx.restore();
      /* the duvet: from the neck to past the feet. Its top drapes over the body (an envelope of
         short arcs, so it bridges the waist instead of dipping into it), its lower edge lies on
         the mattress, and it falls off past the feet. The top hem is turned down at the shoulders. */
      var T = top(), x0 = bx(NECK) - 1.2, xe = Math.min(BED_L - 0.6, bx(N - 1) + 5);
      var xs = [], tops = [];
      for (x = x0; x <= xe + 1e-6; x += 0.5) {
        var best = -1e9;
        for (k = NECK + 2; k < N; k++) {
          var d = x - bx(k);
          var v = y[k] + body.u[k] + 1.1 - d * d * 0.07;
          if (v > best) best = v;
        }
        var fl = T - dentAt(x) + 0.3;
        xs.push(x); tops.push(Math.max(best, fl));
      }
      /* the hem: it rises from the mattress under the chin to the top of the shoulder, a soft
         slope rather than a wall */
      var floor0 = T - dentAt(x0) + 0.3, RISE = 5.2;
      for (k = 0; k < xs.length; k++) {
        var t = (xs[k] - x0) / RISE;
        if (t >= 1) break;
        var e2 = 1 - Math.pow(1 - t, 2.2);
        tops[k] = floor0 + (tops[k] - floor0) * (0.18 + 0.82 * e2);
      }
      for (var pass = 0; pass < 2; pass++) for (k = 1; k < tops.length - 1; k++) tops[k] = (tops[k - 1] + tops[k] * 2 + tops[k + 1]) / 4;
      pts = [];
      for (k = 0; k < xs.length; k++) pts.push([X(xs[k]), Y(tops[k])]);
      ctx.save();
      ctx.beginPath();
      ctx.moveTo(X(x0), Y(T - dentAt(x0) + 0.15));
      catmull(pts, false);
      for (x = xe; x >= x0 - 1e-6; x -= 0.5) ctx.lineTo(X(x), Y(T - dentAt(x) + 0.15));
      ctx.closePath();
      var gd = ctx.createLinearGradient(0, Y(T + 17), 0, Y(T));
      gd.addColorStop(0, css(C.duvet)); gd.addColorStop(1, css(C.duvetLo));
      ctx.fillStyle = gd;
      ctx.shadowColor = css(C.shadow, C.dark ? 0.45 : 0.13); ctx.shadowBlur = 12 * dpr; ctx.shadowOffsetY = 2 * dpr;
      ctx.fill();
      ctx.restore();
      ctx.save();
      ctx.lineCap = 'round';
      /* soft quilting lines that follow the top */
      ctx.strokeStyle = css(C.duvetLine, 0.5); ctx.lineWidth = Math.max(0.8, S * 0.09);
      [3.2, 6.6].forEach(function (off) {
        ctx.beginPath();
        var on = false;
        for (var q = 0; q < xs.length; q++) {
          var ty = tops[q] - off, fy = T - dentAt(xs[q]) + 1.4;
          if (ty < fy || xs[q] < x0 + 3.4) { on = false; continue; }
          if (!on) { ctx.moveTo(X(xs[q]), Y(ty)); on = true; } else ctx.lineTo(X(xs[q]), Y(ty));
        }
        ctx.stroke();
      });
      /* the edge of the duvet */
      ctx.strokeStyle = css(C.duvetLine, 0.95); ctx.lineWidth = Math.max(1, S * 0.13);
      ctx.beginPath(); catmull(pts, false); ctx.stroke();
      /* the turned-down hem */
      var hk = Math.min(xs.length - 1, Math.round(4.4 / 0.5));
      ctx.beginPath();
      ctx.moveTo(X(x0), Y(T - dentAt(x0) + 0.15));
      for (k = 0; k <= hk; k++) ctx.lineTo(pts[k][0], pts[k][1] + 1.2);
      ctx.bezierCurveTo(X(xs[hk] + 0.6), Y(tops[hk] - (tops[hk] - floor0) * 0.4), X(xs[hk] + 0.3), Y(T - dentAt(xs[hk]) + 1), X(xs[hk] - 0.2), Y(T - dentAt(xs[hk]) + 0.15));
      ctx.closePath();
      var gm = ctx.createLinearGradient(X(x0), 0, X(xs[hk]), 0);
      gm.addColorStop(0, css(C.hem)); gm.addColorStop(1, css(shade(C.hem, 0.96)));
      ctx.fillStyle = gm; ctx.fill();
      ctx.strokeStyle = css(C.duvetLine, 0.9); ctx.lineWidth = Math.max(1, S * 0.11); ctx.stroke();
      ctx.restore();
    }
    function drawSpine(sp) {
      var i, x, a = Math.round(0.16 * (N - 1)), b = sp.b, s0 = sp.a;
      var ya = body.y[s0];
      /* the line a straight spine would follow */
      ctx.setLineDash([2, 5]); ctx.lineWidth = 1.4; ctx.lineCap = 'round'; ctx.strokeStyle = css(C.navy, 0.55);
      ctx.beginPath(); ctx.moveTo(X(bx(a)) - 4, Y(ya)); ctx.lineTo(X(bx(b)) + 26, Y(ya)); ctx.stroke();
      ctx.setLineDash([]);
      /* the spine: a chain of vertebrae over the duvet */
      ctx.lineWidth = Math.max(1.5, S * 0.18); ctx.strokeStyle = css(C.navy, 0.85);
      ctx.beginPath();
      for (i = a; i <= b; i++) { x = X(bx(i)); if (i === a) ctx.moveTo(x, Y(body.y[i])); else ctx.lineTo(x, Y(body.y[i])); }
      ctx.stroke();
      for (i = a; i <= b; i++) {
        x = X(bx(i));
        var end = i === a || i === b, r = Math.max(end ? 2.4 : 1.7, S * (end ? 0.42 : 0.3));
        ctx.beginPath(); ctx.arc(x, Y(body.y[i]), r + 1.3, 0, Math.PI * 2); ctx.fillStyle = css(C.duvet, 1); ctx.fill();
        ctx.beginPath(); ctx.arc(x, Y(body.y[i]), r, 0, Math.PI * 2); ctx.fillStyle = css(end ? C.accent : C.navy, 1); ctx.fill();
      }
    }
    function zonesVis() { var v = 1; if (supTo === SUPPORT.continuous) v = 1 - ease(supT); if (supFrom === SUPPORT.continuous && supTo === SUPPORT.continuous) v = 0; return v; }
    function drawLabels(sp) {
      if (labelsOn < 0.02) return;
      var T = top(), sup = supTo, a = labelsOn;
      ctx.font = '600 ' + Math.round(clamp(S * 1.15, 10.5, 13.5)) + 'px Inter, system-ui, sans-serif';
      ctx.textBaseline = 'top'; ctx.textAlign = 'center';
      var yb = PLINTH - 1.1;
      function bracket(x0, x1, text, strong, short) {
        var room = (x1 - x0) * S - 8;
        if (ctx.measureText(text).width > room) text = short || '';
        if (text && ctx.measureText(text).width > room) text = '';
        ctx.strokeStyle = css(strong ? C.navy : C.label, 0.55 * a); ctx.lineWidth = 1;
        ctx.beginPath(); ctx.moveTo(X(x0) + 2, Y(yb) - 4); ctx.lineTo(X(x0) + 2, Y(yb)); ctx.lineTo(X(x1) - 2, Y(yb)); ctx.lineTo(X(x1) - 2, Y(yb) - 4); ctx.stroke();
        ctx.fillStyle = css(strong ? C.navy : C.label, a);
        if (text) ctx.fillText(text, X((x0 + x1) / 2), Y(yb) + 4);
      }
      if (sup === SUPPORT.continuous) bracket(0, BED_L, 'The same support head to toe', false, 'Same support throughout');
      else if (sup === SUPPORT.zoned) { bracket(0, 26, 'Softer: shoulders', false, 'Softer'); bracket(26, 50, 'Firmer: waist, hips, lower back', true, 'Firmer'); bracket(50, BED_L, 'Legs', false, ''); }
      else { bracket(10, 25, 'Softest: shoulders', false, 'Softest'); bracket(30, 48, 'Firmest: waist, hips', true, 'Firmest'); }
    }

    function draw() {
      if (!VIEW) return;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, W, H);
      buildStack();
      dents();
      drawBed();
      drawPillow();
      drawSleeper();
      var sp = spine();
      drawSpine(sp);
      drawLabels(sp);
      readout(sp);
      if (!drawn) { drawn = true; stage.classList.add('is-live'); }
    }
    var drawn = false;

    /* ---------- the spirit level ---------- */
    var lastWord = '';
    function readout(sp) {
      var g = clamp(sp.tilt * 8, -26, 26);
      if (bubbleEl) bubbleEl.style.transform = 'translateX(' + (-g).toFixed(1) + 'px)';
      var fast = 0;
      for (var q = 0; q < N; q++) fast = Math.max(fast, Math.abs(body.y[q] - prev[q]) * 240);
      var lifted = held || fast > 6;
      var back = poseTo === 1, level = Math.abs(sp.tilt) < 0.9 && sp.sag < 0.8;
      var word = lifted ? '' : level ? 'Spine level' : sp.tilt > 0 ? (back ? 'Hips sink lower' : 'Shoulders held high') : (back ? 'Shoulders sink lower' : 'Hips held high');
      if (levelEl) levelEl.classList.toggle('is-level', word === 'Spine level');
      if (word !== lastWord) {
        lastWord = word;
        if (levelText) levelText.textContent = word;
      }
    }

    /* ---------- running ---------- */
    var raf = 0, last = 0, onScreen = false, quiet = 0, script = [];
    function frame(now) {
      var dt = Math.min(0.05, (now - last) / 1000 || 0.016); last = now;
      if (!VIEW) { raf = 0; return; }
      runScript(now);
      if (supT < 1) supT = Math.min(1, supT + dt / 0.9);
      if (pose !== poseTo) { pose += clamp(poseTo - pose, -dt / 0.75, dt / 0.75); body.setPose(ease(clamp(pose, 0, 1))); }
      var labelTo = held || lift > 0.5 ? 0 : 1;
      labelsOn += (labelTo - labelsOn) * (1 - Math.exp(-dt / 0.25));
      var n = Math.max(1, Math.round(dt * 240));
      for (var k = 0; k < n; k++) step(dt / n);
      draw();
      var moving = 0;
      for (var i = 0; i < N; i++) moving = Math.max(moving, Math.abs(body.y[i] - prev[i]) * 240);
      quiet = moving < 0.05 && !held && supT >= 1 && pose === poseTo && !script.length && Math.abs(labelsOn - labelTo) < 0.01 ? quiet + 1 : 0;
      raf = onScreen && quiet < 20 ? requestAnimationFrame(frame) : 0;
    }
    function wake() { if (!raf && onScreen && VIEW) { quiet = 0; last = performance.now(); raf = requestAnimationFrame(frame); } }
    function runScript(now) {
      while (script.length && now >= script[0].at) { var s = script.shift(); s.fn(); }
    }

    /* ---------- controls ---------- */
    function setSupport(key, quietly) {
      if (!SUPPORT[key] || (SUPPORT[key] === supTo && supT >= 1)) return;
      supFrom = supTo; supTo = SUPPORT[key]; supT = quietly ? 1 : 0; mode = key;
      stage.setAttribute('data-support', key);
      Array.prototype.forEach.call(root.querySelectorAll('[data-support-pick]'), function (b) { b.setAttribute('aria-pressed', String(b.getAttribute('data-support-pick') === key)); });
      if (statusEl) statusEl.textContent = (root.querySelector('[data-support-pick="' + key + '"] [data-support-name]') || {}).textContent || '';
      wake();
    }
    function setPose(back) {
      poseTo = back ? 1 : 0;
      Array.prototype.forEach.call(root.querySelectorAll('[data-pose]'), function (b) { b.setAttribute('aria-pressed', String((b.getAttribute('data-pose') === 'back') === !!back)); });
      wake();
    }
    /* the cards' prices follow the size picker: the lowest price, for the chosen size, among
       the rows each card names */
    var cards = Array.prototype.slice.call(root.querySelectorAll('[data-support-pick][data-support-rows]'));
    var sizeCache = {};
    function sizesOf(rowId) {
      if (sizeCache[rowId]) return sizeCache[rowId];
      var row = document.getElementById(rowId), out = [];
      if (row) Array.prototype.forEach.call(row.querySelectorAll('[data-sizes-json]'), function (sj) { try { out.push(JSON.parse(sj.textContent)); } catch (err) { /* skip */ } });
      return (sizeCache[rowId] = out);
    }
    function money(c) { return '$' + Math.round(c / 100).toLocaleString('en-US'); }
    function cardPrices() {
      var pressed = root.querySelector('[data-sizes] button[aria-pressed="true"]');
      var size = pressed ? pressed.getAttribute('data-size') : root.getAttribute('data-default-size');
      cards.forEach(function (card) {
        var low = 0;
        (card.getAttribute('data-support-rows') || '').split(',').filter(Boolean).forEach(function (id) {
          sizesOf(id).forEach(function (sz) { var e = sz[size]; if (e && (!low || e.price < low)) low = e.price; });
        });
        var el = card.querySelector('[data-support-from]'), txt = low ? 'from ' + money(low) : 'Not made in ' + size;
        if (el && el.textContent !== txt) el.textContent = txt;
      });
    }
    var sizeGroup = root.querySelector('[data-sizes]');
    if (sizeGroup && 'MutationObserver' in window && cards.length) new MutationObserver(cardPrices).observe(sizeGroup, { attributes: true, subtree: true, attributeFilter: ['aria-pressed'] });

    root.addEventListener('click', function (e) {
      var s = e.target.closest('[data-support-pick]');
      if (s && root.contains(s)) { cancelIntro(); setSupport(s.getAttribute('data-support-pick')); return; }
      var p = e.target.closest('[data-pose]');
      if (p && root.contains(p)) { cancelIntro(); setPose(p.getAttribute('data-pose') === 'back'); }
    });

    /* ---------- pick the sleeper up and drop them ---------- */
    function local(e) { var b = canvas.getBoundingClientRect(); return { x: (e.clientX - b.left - OX) / S, y: (OY - (e.clientY - b.top)) / S }; }
    function onBody(p) {
      var i = Math.round((p.x - body.x0 - shiftX) / body.dx);
      if (i < 0 || i >= N) return false;
      return p.y <= body.y[i] + body.u[i] + 1.5 && p.y >= body.y[i] - body.w[i] - 1;
    }
    var last2 = null;
    canvas.addEventListener('pointerdown', function (e) {
      var p = local(e);
      if (!onBody(p)) return;
      cancelIntro();
      held = { base: Array.prototype.slice.call(body.y), lift: 0, y0: p.y, x0: p.x, sx: shiftX, id: e.pointerId };
      last2 = { y: p.y, t: performance.now(), vy: 0 };
      try { canvas.setPointerCapture(e.pointerId); } catch (err) { /* fine */ }
      stage.classList.add('is-holding');
      wake();
    });
    canvas.addEventListener('pointermove', function (e) {
      var p = local(e);
      if (!held) { canvas.style.cursor = onBody(p) ? 'grab' : ''; return; }
      if (e.pointerId !== held.id) return;
      held.lift = clamp(p.y - held.y0, -1, 9);
      var nsx = clamp(held.sx + (p.x - held.x0), -2.5, 9);
      var d = nsx - shiftX; shiftX = nsx;
      if (d) for (var i = 0; i < N; i++) held.base[i] += 0;
      var now = performance.now();
      if (last2) { last2.vy = (p.y - last2.y) / Math.max(0.008, (now - last2.t) / 1000); last2.y = p.y; last2.t = now; }
      if (e.cancelable) e.preventDefault();
      wake();
    });
    function release(e) {
      if (!held || (e && e.pointerId !== held.id)) return;
      var vy = last2 ? clamp(last2.vy, -80, 80) : 0;
      kick(vy, 1 / 240);
      held = null; stage.classList.remove('is-holding');
      wake();
    }
    canvas.addEventListener('pointerup', release);
    canvas.addEventListener('pointercancel', release);

    /* ---------- the opening: the sleeper lies down on a bed with continuous support, then the
       bed switches to Zoned Support and the spine comes level. Once, when first on screen. ---------- */
    var introDone = false;
    function cancelIntro() { script = []; introDone = true; }
    function intro() {
      if (introDone) return;
      introDone = true;
      var t0 = performance.now(), start = mode;
      setSupport('continuous', true);
      placeAbove(7);
      script = [
        { at: t0 + 2300, fn: function () { setSupport(start === 'continuous' ? 'zoned' : start); } }
      ];
      wake();
    }

    function repaint() {
      readColors(); layout();
      if (!VIEW) return;
      if (!body.ready) {
        body.ready = true;
        placeAbove(0); settle(900);
      }
      draw();
      wake();
    }

    var begin = function (visible) {
      onScreen = visible;
      if (visible && !still && !window.__moCzNoIntro) intro();
      if (visible) wake();
    };
    repaint();
    if (still) { stage.classList.add('is-still'); }
    else if ('IntersectionObserver' in window) new IntersectionObserver(function (es) { begin(es[0].isIntersecting); }, { threshold: 0.25 }).observe(canvas);
    else begin(true);

    /* test hooks for QA scripts; harmless in production */
    stage.__zones = {
      support: setSupport, pose: setPose, busy: function () { return !!raf; },
      spine: function () { return spine(); },
      lift: function (h) { held = { base: Array.prototype.slice.call(body.y), lift: h, y0: 0, x0: 0, sx: shiftX, id: -1 }; wake(); },
      drop: function () { held = null; wake(); },
      settle: function () { settle(1200); draw(); },
      measure: function (key, back) {
        supFrom = supTo = SUPPORT[key]; supT = 1; pose = poseTo = back ? 1 : 0; body.setPose(back ? 1 : 0);
        placeAbove(0.5); settle(2400); draw();
        var sp = spine(), T = top(), sh = Math.round(0.205 * (N - 1)), hp = Math.round(0.475 * (N - 1));
        sp.shoulderSink = T - (body.y[sh] - body.w[sh]); sp.hipSink = T - (body.y[hp] - body.w[hp]);
        return sp;
      },
      cancel: cancelIntro
    };
    return repaint;
  };
  /* ==================================================================================
   * Row drawing: a Casper side view with the cover cut back
   * Height is to scale, 5 units per inch, like every lineup drawing. Casper does not publish
   * layer depths, so the split between layers is illustrative.
   * Kinds: cover, cover-cool (QuickCool), breathe, breathe-plus, breathe-zoned, breathe-memory,
   * pcm (phase change material), heatdelete (graphite bands; "three" or "six" in the name sets the
   * count), align, align-plus, align-zoned, align-channels, core, core-plus (foam pillars), coils, base.
   * ================================================================================== */
  var cutN = 0;
  var SHARE = { pcm: 0.3, heatdelete: 0.9, base: 0.55, coils: 2.7, core: 2.1, 'core-plus': 2.3 };
  draws.casper = function (version, inches, kinds) {
    var lis = version ? Array.prototype.slice.call(version.querySelectorAll('.mo-lineup__layers li')) : [];
    var src = lis.length ? lis.map(function (li) { return { k: (li.getAttribute('data-kind') || '').trim(), t: (li.textContent || '').toLowerCase() }; })
      : kinds.map(function (k) { return { k: k, t: '' }; });
    var layers = src.filter(function (l) { return l.k; });
    var id = 'mo-ccut-' + (++cutN);
    var VW = 204, VH = 118, X0 = 12, X1 = 198, U = 5, FLOOR = 107, BOTTOM = FLOOR - 0.5;
    var h = Math.max(48, Math.min(inches * U, 84)), top = BOTTOM - h;
    var CUT = X0 + (X1 - X0) * 0.27, slope = 0.16;
    function cx(y) { return CUT + (y - top) * slope; }
    var cover = layers.filter(function (l) { return /^cover/.test(l.k); })[0] || { k: 'cover', t: '' };
    var body = layers.filter(function (l) { return !/^cover/.test(l.k); });
    var COV = 2.6, room = h - COV, tot = 0;
    body.forEach(function (l) { l.w = SHARE[l.k] || 1; tot += l.w; });
    body.forEach(function (l) { l.hh = room * l.w / tot; });
    var o = '<defs><linearGradient id="' + id + 'g" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#fff" stop-opacity=".3"/><stop offset=".5" stop-color="#fff" stop-opacity="0"/><stop offset="1" stop-color="#000" stop-opacity=".06"/></linearGradient>' +
      '<linearGradient id="' + id + 'p" x1="0" y1="0" x2="1" y2="0"><stop offset="0" class="k-pcm-a"/><stop offset=".5" class="k-pcm-b"/><stop offset="1" class="k-pcm-c"/></linearGradient>' +
      '<clipPath id="' + id + 'c"><path d="M' + n1(cx(top)) + ',' + n1(top) + 'H' + (X1 - 3) + 'Q' + X1 + ',' + n1(top) + ' ' + X1 + ',' + n1(top + 3) +
      'V' + n1(BOTTOM - 2) + 'Q' + X1 + ',' + n1(BOTTOM) + ' ' + (X1 - 2) + ',' + n1(BOTTOM) + 'H' + n1(cx(BOTTOM)) + 'Z"/></clipPath></defs>';
    o += '<ellipse class="k-shadow" cx="' + ((X0 + X1) / 2) + '" cy="' + (FLOOR + 2.2) + '" rx="' + ((X1 - X0) / 2 + 4) + '" ry="2.2"/>';
    o += '<path class="k-dim" d="M5,' + n1(top) + 'V' + n1(BOTTOM) + 'M2.4,' + n1(top) + 'h5.2M2.4,' + n1(BOTTOM) + 'h5.2"/>';
    o += '<g clip-path="url(#' + id + 'c)">';
    var y = top + COV, n = body.length;
    body.forEach(function (l, idx) {
      var y0 = y, y1 = y + l.hh, k = l.k, L = (n - idx) * 3, xs = cx(y0) + 2.5, xe = X1 - 2.5, yy, xx, r, i;
      var lifts = !/^(coils|base|core|core-plus)$/.test(k);
      o += '<g class="' + (lifts ? 'c-lift ' : '') + 'k-l k-' + k + '"' + (lifts ? ' style="--lift:' + n1(L) + '"' : '') + '>';
      if (k === 'pcm') o += '<rect class="k-pcmfill" x="' + X0 + '" y="' + n1(y0) + '" width="' + (X1 - X0) + '" height="' + n1(Math.max(0.6, l.hh - 0.3)) + '" style="fill:url(#' + id + 'p)"/>';
      else o += '<rect class="k-fill" x="' + X0 + '" y="' + n1(y0) + '" width="' + (X1 - X0) + '" height="' + n1(Math.max(0.6, l.hh - 0.4)) + '"/>';
      if (/^breathe/.test(k) && k !== 'breathe-memory') {
        /* open-cell foam: rows of round cells */
        var pitch = k === 'breathe-plus' ? 3.2 : 4.2, rows = Math.max(1, Math.round(l.hh / 3.2));
        for (r = 0; r < rows; r++) {
          yy = y0 + (r + 0.5) * l.hh / rows;
          for (xx = xs + 1.5 + (r % 2) * pitch / 2; xx < xe; xx += pitch) o += '<circle class="k-cell" cx="' + n1(xx) + '" cy="' + n1(yy) + '" r="' + (k === 'breathe-plus' ? '.9' : '.75') + '"/>';
        }
      }
      if (k === 'breathe-zoned' || k === 'align-zoned' || k === 'align-channels') {
        /* zones along the bed: softer at the shoulders, firmer through the middle */
        var parts = k === 'align-channels' ? [0.1, 0.17, 0.07, 0.11, 0.13, 0.22, 0.2] : [0.33, 0.32, 0.35];
        var firm = k === 'align-channels' ? [0, 0, 0, 1, 1, 0, 0] : [0, 1, 0];
        var zx = xs;
        parts.forEach(function (f, zi) {
          var w2 = (xe - xs) * f;
          o += '<rect class="k-zone' + (firm[zi] ? ' is-firm' : '') + '" x="' + n1(zx + 0.5) + '" y="' + n1(y0 + 0.6) + '" width="' + n1(Math.max(0.5, w2 - 1)) + '" height="' + n1(Math.max(1, l.hh - 1.6)) + '" rx="1.2"/>';
          if (k === 'align-channels' && !firm[zi]) for (xx = zx + 2; xx < zx + w2 - 1; xx += 2.4) o += '<path class="k-channel" d="M' + n1(xx) + ',' + n1(y0 + 1.4) + 'V' + n1(y1 - 1.8) + '"/>';
          zx += w2;
        });
      }
      if (k === 'align' || k === 'align-plus' || k === 'breathe-memory') {
        var st = k === 'align-plus' ? 3.6 : 4.4;
        for (yy = y0 + 1.6, r = 0; yy < y1 - 1; yy += st * 0.7, r++)
          for (xx = xs + (r % 2) * st / 2 + 1; xx < xe; xx += st) o += '<circle class="k-pore" cx="' + n1(xx) + '" cy="' + n1(yy) + '" r=".45"/>';
      }
      if (k === 'heatdelete') {
        /* thin graphite bands laid across the bed: three in Snow, six in Snow Max */
        var count = /six|6/.test(l.t) ? 6 : 3, span = xe - xs;
        for (yy = y0 + 1.4, r = 0; yy < y1 - 0.8; yy += 3, r++)
          for (xx = xs + (r % 2) * 2 + 1; xx < xe; xx += 4) o += '<circle class="k-pore" cx="' + n1(xx) + '" cy="' + n1(yy) + '" r=".4"/>';
        for (i = 0; i < count; i++) {
          var bxx = xs + span * (0.1 + 0.8 * i / (count - 1)), bw = count > 3 ? 7 : 9;
          o += '<rect class="k-band" x="' + n1(bxx - bw / 2) + '" y="' + n1(y0 + 1) + '" width="' + bw + '" height="' + n1(Math.max(1, l.hh - 2.2)) + '" rx=".8"/>';
          o += '<path class="k-band-hi" d="M' + n1(bxx - bw / 2 + 1) + ',' + n1(y0 + 1.7) + 'H' + n1(bxx + bw / 2 - 1) + '"/>';
          /* heat leaving along the band */
          o += '<path class="k-heat" d="M' + n1(bxx - 1.6) + ',' + n1(y0 - 0.2) + 'q.8,-1.4 0,-2.6M' + n1(bxx + 1.6) + ',' + n1(y0 - 0.2) + 'q.8,-1.4 0,-2.6"/>';
        }
      }
      if (k === 'core' || k === 'core-plus') {
        for (yy = y0 + 2, r = 0; yy < y1 - 1; yy += 3.4, r++)
          for (xx = xs + (r % 2) * 2.4 + 1; xx < xe; xx += 4.8) o += '<circle class="k-pore" cx="' + n1(xx) + '" cy="' + n1(yy) + '" r=".5"/>';
        if (k === 'core-plus') {
          /* Core+: foam pillars built into the core */
          for (xx = xs + 4; xx < xe - 3; xx += 9) o += '<rect class="k-pillar" x="' + n1(xx) + '" y="' + n1(y0 + 1.2) + '" width="4.4" height="' + n1(l.hh * 0.62) + '" rx="2.2"/>';
        }
      }
      if (k === 'coils') {
        var cnt = 10, pt = (xe - xs - 6) / cnt;
        for (i = 0; i < cnt; i++) {
          var cc = xs + pt * (i + 0.5), py0 = y0 + 0.7, py1 = y1 - 0.7;
          o += '<rect class="k-pocket" x="' + n1(cc - pt * 0.46) + '" y="' + n1(py0) + '" width="' + n1(pt * 0.92) + '" height="' + n1(py1 - py0) + '" rx="2.2"/>';
          var turns = 5, sp = py1 - py0 - 2.4, rx = pt * 0.32, ry = Math.min(1.1, sp / turns * 0.3), back = '', front = '', on = -1, steps = turns * 16;
          for (var j = 0; j <= steps; j++) {
            var an = j / steps * turns * Math.PI * 2, hx = cc + Math.cos(an) * rx, hy = py0 + 1.2 + sp * j / steps + Math.sin(an) * ry, near = Math.sin(an) >= 0 ? 1 : 0;
            var p2 = n1(hx) + ',' + n1(hy);
            if (near !== on) { if (near) front += 'M' + p2; else back += 'M' + p2; on = near; }
            else if (near) front += 'L' + p2; else back += 'L' + p2;
          }
          o += '<path class="k-coil k-coil--back" d="' + back + '"/><path class="k-coil" d="' + front + '"/>';
        }
        /* the reinforced edge */
        o += '<rect class="k-edge" x="' + n1(xe - 5.5) + '" y="' + n1(y0 + 0.5) + '" width="5" height="' + n1(l.hh - 1) + '" rx="1"/>';
      }
      if (!/^(coils|pcm)$/.test(k)) o += '<rect x="' + X0 + '" y="' + n1(y0) + '" width="' + (X1 - X0) + '" height="' + n1(Math.max(0.6, l.hh - 0.4)) + '" fill="url(#' + id + 'g)"/>';
      o += '<path class="k-seam" d="M' + n1(cx(y1)) + ',' + n1(y1 - 0.2) + 'H' + X1 + '"/>';
      o += '</g>';
      y = y1;
    });
    /* the cover over the cut: a thin knit lid that lifts with the layers */
    var cool = cover.k === 'cover-cool';
    o += '<g class="c-lift k-l k-' + cover.k + '" style="--lift:' + n1(n * 3 + 4) + '">';
    o += '<path class="k-fill" d="M' + X0 + ',' + n1(top + 0.6) + 'Q' + X0 + ',' + n1(top) + ' ' + (X0 + 2) + ',' + n1(top) + 'H' + (X1 - 3) + 'Q' + X1 + ',' + n1(top) + ' ' + X1 + ',' + n1(top + 2) + 'V' + n1(top + COV - 0.3) + 'H' + X0 + 'Z"/>';
    var kn = 'M' + n1(cx(top) + 2) + ',' + n1(top + 1.3);
    for (var kx = cx(top) + 2, up2 = 1; kx < X1 - 4; kx += 2.6, up2 = -up2) kn += 'Q' + n1(kx + 1.3) + ',' + n1(top + 1.3 - up2 * 0.7) + ' ' + n1(kx + 2.6) + ',' + n1(top + 1.3);
    o += '<path class="k-knit" d="' + kn + '"/>';
    o += '</g>';
    o += '</g>';
    /* the finished side, left of the cut: a soft knit top band, a grey knit side, a navy tape */
    var band = top + COV + 1.8;
    o += '<path class="k-topband' + (cool ? ' is-cool' : '') + '" d="M' + (X0 + 2) + ',' + n1(top) + 'H' + n1(cx(top)) + 'L' + n1(cx(band)) + ',' + n1(band) + 'H' + X0 + 'V' + n1(top + 2) + 'Q' + X0 + ',' + n1(top) + ' ' + (X0 + 2) + ',' + n1(top) + 'Z"/>';
    o += '<path class="k-panel" d="M' + X0 + ',' + n1(band) + 'H' + n1(cx(band)) + 'L' + n1(cx(BOTTOM)) + ',' + n1(BOTTOM) + 'H' + (X0 + 3) + 'Q' + X0 + ',' + n1(BOTTOM) + ' ' + X0 + ',' + n1(BOTTOM - 3) + 'Z"/>';
    for (var bx2 = X0 + 3.2; bx2 < cx(BOTTOM) - 1.5; bx2 += 3.2) {
      var rt = Math.max(band + 1.2, top + (bx2 - CUT) / slope + 1);
      if (rt < BOTTOM - 2) o += '<path class="k-rib" d="M' + n1(bx2) + ',' + n1(rt) + 'V' + n1(BOTTOM - 1.4) + '"/>';
    }
    o += '<path class="k-tape" d="M' + (X0 + 0.5) + ',' + n1(band) + 'H' + n1(cx(band) - 0.3) + 'M' + (X0 + 1.5) + ',' + n1(BOTTOM - 0.6) + 'H' + n1(cx(BOTTOM - 0.6) - 0.3) + '"/>';
    if (cool) o += '<path class="k-stripe" d="M' + (X0 + 0.6) + ',' + n1(band + 2.4) + 'H' + n1(cx(band + 2.4) - 0.4) + '"/>';
    o += '<path class="k-edge-cut" d="M' + n1(cx(top)) + ',' + n1(top) + 'L' + n1(cx(BOTTOM)) + ',' + n1(BOTTOM) + '"/>';
    o += '<path class="k-floor" d="M1,' + (FLOOR + 0.6) + 'H' + (VW - 1) + '"/>';
    return '<svg viewBox="0 0 ' + VW + ' ' + VH + '" aria-hidden="true" focusable="false">' + o + '</svg>';
  };
})();
